<template>
  <VCard
      :max-height="$vuetify.breakpoint.xs ? '100vh' : undefined"
      :height="!$vuetify.breakpoint.xs ? '80vh' : undefined"
      class="d-flex flex-column"
      :class="{
          'overflow-auto': $vuetify.breakpoint.xs,
          'overflow-hidden': !$vuetify.breakpoint.xs,
        }"
      :loading="isLoadingSelectedOpsZone"
      :disabled="isLoadingSelectedOpsZone"
  >
   <template #progress> 
      <Spinner />
    </template>
    <ScrollButtons
      v-if="$vuetify.breakpoint.xs"
      :selectedPoiId="selectedPoiId"
      :selectedPoiType="selectedPoiType"
      :directionContainerScrollPosition="directionContainerScrollPosition"
      @scrollToPoiSelection="(type, poi, position) => scrollToPoiSelection(type, poi, position)"
      @scrolToTop="scrolToTop"
    />
    <VCardTitle
      class="p-0 box-shadow-elevation-4 position-relative z-index-1000"
      v-show="!isLoading && isMapReady"
    >
      <Stepper
        class="px-1"
        :currentStep="currentStep"
        :steps="getSteps"
        @onStepClick="changeStep"
      />
      <VBtn
        class="close-icon mr-3 grey-lighten-4 box-shadow-elevation-6"
        icon
        @click="$emit('closeDialog')"
      >
        <VIcon color="textPrimary">mdi-close</VIcon>
      </VBtn>
    </VCardTitle>
    <VCardText
      v-show="!isLoading && isMapReady"
      v-scroll.self="onScroll"
      id="direction-dialog-container"
      ref="directionDialogContainer"
      class="p-0 shadow-none flex-grow-1 overflow-auto"
    >
      <AddressSelection
        ref="addressSelection"
        :isLoading="isLoading"
        :key="currentStep"
        :error="error"
        :poiList="poiList"
        :pickedLocation="getSelectedLocation"
        :opsZoneList="opsZoneList"
        :communeCoordinates="communeCoordinates"
        :isNightCardsSupport="isNightCardsSupport"
        :additionalMapZonesColor="additionalMapZonesColor"
        :opsZoneColor="opsZoneColor"
        :opsZoneMapFill="opsZoneMapFill"
        :opsZoneMapStroke="opsZoneMapStroke"
        :communeMapFill="communeMapFill"
        :communeMapStroke="communeMapStroke"
        :poiListWithoutGrouping="poiListWithoutGrouping"
        :currentStep="currentStep"
        :isDeparturePlaceFilled="getIsDeparturePlaceFilled"
        :isArrivalPlaceFilled="getIsArrivalPlaceFilled"
        :additionalHeightForMap="additionalHeightForMap"
        @setLocation="setLocation"
        @setError="setError"
        @updatePoiType="updatePoiType"
        @updatePoiId="updatePoiId"
        @recalculateHeight="recalculateHeight"
        @map-ready="handleMapReady"
      />
    </VCardText>
    <DirectionDialogSkeletonLoader
      v-if="isLoading || !isMapReady"
      @closeDialog="$emit('closeDialog')"
    />
  </VCard>
</template>

<script lang="ts">
export default {
  name: 'DirectionDialog',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from '../../../composables';
import Stepper from './stepper/Stepper.vue';
import Spinner from './Spinner.vue';
import AddressSelection from './directionDialog/AddressSelection.vue';
import ScrollButtons from './directionDialog/ScrollButtons.vue';
import DirectionDialogSkeletonLoader from './DirectionDialogSkeletonLoader.vue';

const props = defineProps({
  error: {
    type: String,
    default: '',
  },
  currentStep: {
    type: String,
    default: 'pickUp',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isLoadingSelectedOpsZone: {
    type: Boolean,
    default: false,
  },
  getArrivalPlace: {
    type: Object,
    default: () => ({}),
  },
  getDeparturePlace: {
    type: Object,
    default: () => ({}),
  },
  poiList: {
    type: Object,
    default: () => ({}),
  },
  getIsArrivalPlaceFilled: {
    type: Boolean,
    default: false,
  },
  getIsDeparturePlaceFilled: {
    type: Boolean,
    default: false,
  },
  poiListWithoutGrouping: {
    type: Array,
    default: () => ([]),
  },
  opsZoneList: {
    type: Array,
    default: () => ([]),
  },
  stepsMeta: {
    type: Object,
    default: () => ({}),
  },
  communeCoordinates: {
    type: Array,
    default: () => [],
  },
  isNightCardsSupport: {
    type: Boolean,
    default: false,
  },
  additionalMapZonesColor: {
    type: String,
    default: '',
  },
  opsZoneColor: {
    type: String,
    default: '',
  },
  opsZoneMapFill: {
    type: String,
    default: '',
  },
  opsZoneMapStroke: {
    type: String,
    default: '',
  },
  communeMapFill: {
    type: String,
    default: '',
  },
  communeMapStroke: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['closeDialog', 'loadState', 'setError', 'changeStep', 'setLocation']);

const { t } = useI18n();

const directionDialogContainer = ref(null);
const addressSelection = ref(null);
const directionContainerScrollPosition = ref(0);
const selectedPoiId = ref(null);
const selectedPoiType = ref(null);
const isMapReady = ref(false);
const additionalHeightForMap = ref(0);

const getSteps = computed(() => [{
  title: t('bookingForm.pickUpAddress'),
  subTitle: props.getDeparturePlace?.address,
  isComplete: props.getIsDeparturePlaceFilled,
  value: props.stepsMeta.pickUp,
},
  {
    title: t('bookingForm.dropOffAddress'),
    subTitle: props.getArrivalPlace?.address,
    isComplete: props.getIsArrivalPlaceFilled,
    value: props.stepsMeta.dropOff,
  }]);

const getSelectedLocation = computed(() => {
  if (props.currentStep === props.stepsMeta.pickUp) {
    return props.getDeparturePlace;
  }
  return props.getArrivalPlace;
});

watch(() => props.isLoading, (newVal) => {
  if (!newVal && addressSelection.value) {
    addressSelection.value.mapKey = new Date().toString();
  }
});

function setError(error) {
  emit('setError', error);
}

function handleMapReady() {
  isMapReady.value = true;
  if (addressSelection.value) {
    addressSelection.value.mapKey = new Date().toString();
  }
}

function getDialogContainerEl() {
  const container = directionDialogContainer.value;
  return container?.$el || container;
}

setError('');
emit('loadState');
selectedPoiId.value = getSelectedLocation.value.poiStopId;
selectedPoiType.value = getSelectedLocation.value.type;

async function recalculateHeight() {
  const containerEl = getDialogContainerEl();
  const wrapperHeight = addressSelection.value?.getWrapperClientHeight?.() || 0;
  const containerHeight = containerEl?.clientHeight || 0;
  additionalHeightForMap.value = wrapperHeight > 0
    ? Math.max(0, containerHeight - wrapperHeight)
    : 0;
}

function updatePoiType(poiType) {
  selectedPoiType.value = poiType;
}

function updatePoiId(poiId) {
  selectedPoiId.value = poiId;
}

function onScroll() {
  const containerEl = getDialogContainerEl();
  directionContainerScrollPosition.value = containerEl?.scrollTop || 0;
  addressSelection.value?.setMapFocused?.(directionContainerScrollPosition.value === 0);
}

function scrollToPoiSelection(type, poi, position = 'center') {
  addressSelection.value.scrollToPoiSelection(type, poi, position);
}

function scrolToTop() {
  getDialogContainerEl()?.scroll({ top: 0, behavior: 'smooth' });
}

function invalidateMapSize() {
  addressSelection.value.invalidateMapSize();
}

function changeStep(step, opsZoneIdsList = []) {
  emit('changeStep', { step, opsZoneIdsList });
}

function setLocation(location) {
  emit('setLocation', location);
  selectedPoiId.value = null;
}

defineExpose({
  invalidateMapSize,
});
</script>

<style scoped lang="scss">
.close-icon {
  z-index: 10;
  position: absolute;
  right: 0;
}
</style>
